import { Fragment } from 'react';
import { XIcon } from 'lucide-react';
import dayjs from 'dayjs';

import { EscrowStatusEnum } from 'src/graphql/schema';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EscrowAlertBadge } from 'src/components/Escrow/AlertBadge';
import { cn } from 'src/lib/utils';

import { useEscrowDashboardCard } from '../model/context';

import { Progress } from './Progress';
import { CertificateBadges } from './CertificateBadges';

const ActiveRequestBadge = () => {
  const { viewModel } = useEscrowDashboardCard();
  const {
    status,
    changesRequested,
    escrowTermination,
    pendingActivation,
    hasUnconfirmedProvider,
    hasAccessIssuesProvider,
  } = viewModel;

  if (status === EscrowStatusEnum.Terminated) return null;

  let variant: 'warning' | 'alert' | null = null;
  const isTerminationState = status === EscrowStatusEnum.PendingTermination || Boolean(escrowTermination);

  if (changesRequested) variant = 'warning';
  if (pendingActivation && hasUnconfirmedProvider) variant = 'warning';
  if ((hasUnconfirmedProvider && status === EscrowStatusEnum.Active) || hasAccessIssuesProvider) variant = 'alert';
  if (isTerminationState) variant = 'alert';
  if (!variant) return null;

  return <EscrowAlertBadge variant={variant} className="size-5" iconClassName="size-4.5" />;
};

const Header = () => {
  const { viewModel, config } = useEscrowDashboardCard();
  const { status, userUpdatedAt, onRemove } = viewModel;
  const { statusMap } = config;

  const { label, bgColor, color } = statusMap[status as keyof typeof statusMap] ?? statusMap.rejected;

  return (
    <div className="flex items-start justify-between gap-2 mb-5">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <ActiveRequestBadge />
          <Badge className={cn('shadow-sm rounded', bgColor, color)}>{label}</Badge>
        </div>
        <div className="text-xs text-slate-400">Updated {dayjs(userUpdatedAt).fromNow()}</div>
      </div>

      {status === EscrowStatusEnum.Draft ? (
        <Button variant="ghost" size="icon" className="size-6" onClick={onRemove}>
          <XIcon className="size-4" />
        </Button>
      ) : (
        <Fragment>
          <Progress />
          <CertificateBadges />
        </Fragment>
      )}
    </div>
  );
};

export { Header };
